import React, { useState, useEffect, useCallback } from 'react';
import {
  FiPlus, FiEdit, FiTrash2, FiEye, FiEyeOff, FiMove, FiSave, FiX,
  FiChevronDown, FiChevronRight, FiSearch, FiFilter, FiDownload,
  FiUpload, FiCopy, FiCheck, FiAlertCircle, FiInfo, FiClock,
  FiVideo, FiBook
} from 'react-icons/fi';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { apiUrls, API_CONFIG, getHttpHeaders } from '../config/api';
import { buildTree, filterTree, CSNode, getLevelColor, getLevelName } from './tree/treeHelpers';
import ContentAssetSelector from './ContentAssetSelector';
import BookContentManager from './BookContentManager';

import {
  ContentStructure,
  Subject,
  Grade,
  ContentAsset
} from '../services/enhancedContentStructureService';

interface WizardStep {
  id: number;
  title: string;
  description: string;
  isCompleted: boolean;
  isRequired: boolean;
}

// Sortable Item Component
const SortableItem: React.FC<{
  id: number;
  structure: ContentStructure;
  onEdit: (structure: ContentStructure) => void;
  onDelete: (structure: ContentStructure) => void;
}> = ({ id, structure, onEdit, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        display: 'flex',
        alignItems: 'center',
        padding: '12px',
        backgroundColor: '#f9fafb',
        border: '1px solid #e5e7eb',
        borderRadius: '6px',
        marginBottom: '8px',
        cursor: 'grab'
      }}
      {...attributes}
      {...listeners}
    >
      <FiMove style={{ marginRight: '12px', color: '#9ca3af' }} />
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: '500', marginBottom: '4px' }}>
          {structure.name}
        </div>
        {structure.description && (
          <div style={{ fontSize: '14px', color: '#6b7280' }}>
            {structure.description}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: '8px' }}>
        <button
          onClick={() => onEdit(structure)}
          style={{
            background: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            padding: '6px 10px',
            cursor: 'pointer'
          }}
        >
          <FiEdit size={14} />
        </button>
        <button
          onClick={() => onDelete(structure)}
          style={{
            background: '#ef4444',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            padding: '6px 10px',
            cursor: 'pointer'
          }}
        >
          <FiTrash2 size={14} />
        </button>
      </div>
    </div>
  );
};

const EnhancedContentStructure: React.FC = () => {
  // State Management
  const [structures, setStructures] = useState<ContentStructure[]>([]);
  const [treeData, setTreeData] = useState<CSNode[]>([]);
  const [books, setBooks] = useState<any[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [grades, setGrades] = useState<Grade[]>([]);
  const [filteredSubjects, setFilteredSubjects] = useState<Subject[]>([]);
  const [contentAssets, setContentAssets] = useState<ContentAsset[]>([]);
  const [linkedAssets, setLinkedAssets] = useState<{[lessonId: number]: any[]}>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Context Menu State
  const [contextMenu, setContextMenu] = useState<{
    visible: boolean;
    x: number;
    y: number;
    item: CSNode | null;
  }>({
    visible: false,
    x: 0,
    y: 0,
    item: null
  });
  
  // Inline Edit State
  const [editingItem, setEditingItem] = useState<{
    id: number;
    name: string;
    grade_id?: number;
    subject_id?: number;
  } | null>(null);

  // Structure Edit Modal State (for root structures with grade editing)
  const [structureEditModalOpen, setStructureEditModalOpen] = useState(false);
  const [editingStructureData, setEditingStructureData] = useState<{
    id: number;
    name: string;
    grade_id: number;
    subject_id: number;
  } | null>(null);
  const [editModalFilteredSubjects, setEditModalFilteredSubjects] = useState<any[]>([]);
  
  // Asset Management Modal State
  const [assetModalOpen, setAssetModalOpen] = useState(false);
  const [selectedLessonForAssets, setSelectedLessonForAssets] = useState<CSNode | null>(null);
  const [availableAssets, setAvailableAssets] = useState<any[]>([]);
  const [loadingAssets, setLoadingAssets] = useState(false);
  
  // Asset Preview State
  const [assetPreview, setAssetPreview] = useState<{
    visible: boolean;
    asset: any;
    x: number;
    y: number;
  }>({
    visible: false,
    asset: null,
    x: 0,
    y: 0
  });
  
  // Book Content Management Modal State
  const [bookContentModalOpen, setBookContentModalOpen] = useState(false);
  const [selectedStructureForBooks, setSelectedStructureForBooks] = useState<CSNode | null>(null);
  
  // Add Child Modal State
  const [addChildModalOpen, setAddChildModalOpen] = useState(false);
  const [addChildData, setAddChildData] = useState<{
    parentItem: CSNode | null;
    childType: 'topic' | 'chapter' | 'unit' | 'lesson';
    childName: string;
  }>({
    parentItem: null,
    childType: 'topic',
    childName: ''
  });
  
  // Structure Selection State
  const [selectedStructure, setSelectedStructure] = useState<CSNode | null>(null);

  // Wizard States
  const [showWizard, setShowWizard] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [wizardData, setWizardData] = useState<{
    name: string;
    description: string;
    subject_id: string;
    grade_id: string;
    sort_order: number;
    is_active: boolean;
    topics: Array<{
      id: string;
      name: string;
      description: string;
      chapters: Array<{
        id: string;
        name: string;
        description: string;
        units: Array<{
          id: string;
          name: string;
          description: string;
          lessons: Array<{
            id: string;
            name: string;
            description: string;
            content_asset_ids: number[];
          }>;
        }>;
      }>;
    }>;
  }>({
    name: '',
    description: '',
    subject_id: '',
    grade_id: '',
    sort_order: 0,
    is_active: true,
    topics: []
  });

  // UI States
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSubject, setFilterSubject] = useState<string>('all');
  const [filterGrade, setFilterGrade] = useState<string>('all');
  const [expandedNodes, setExpandedNodes] = useState<Set<number>>(new Set());
  const [selectedStructures, setSelectedStructures] = useState<Set<number>>(new Set());

  // Modal States
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [editingStructure, setEditingStructure] = useState<ContentStructure | null>(null);
  const [deletingStructure, setDeletingStructure] = useState<ContentStructure | null>(null);

  // Wizard Steps Configuration
  const wizardSteps: WizardStep[] = [
    { id: 1, title: 'Basic Information', description: 'Structure name, description, grade, and subject', isCompleted: false, isRequired: true },
    { id: 2, title: 'Topics', description: 'Add topics for this structure', isCompleted: false, isRequired: true },
    { id: 3, title: 'Chapters', description: 'Add chapters for each topic', isCompleted: false, isRequired: true },
    { id: 4, title: 'Units', description: 'Add units for each chapter', isCompleted: false, isRequired: true },
    { id: 5, title: 'Lessons', description: 'Add lessons for each unit with content assets', isCompleted: false, isRequired: true },
    { id: 6, title: 'Preview & Confirm', description: 'Review complete hierarchy before creation', isCompleted: false, isRequired: true }
  ];

  useEffect(() => {
    fetchInitialData();
  }, []);

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setContextMenu(prev => ({ ...prev, visible: false }));
    };
    
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setContextMenu(prev => ({ ...prev, visible: false }));
        setEditingItem(null);
      }
    };

    if (contextMenu.visible) {
      document.addEventListener('click', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [contextMenu.visible]);

  const handleEditStructure = async (structure: ContentStructure) => {
    setLoading(true);
    try {
      const response = await fetch(`${apiUrls.contentLibrary.contentStructures}/${structure.id}`, {
        method: 'PUT',
        headers: { ...getHttpHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: structure.name,
          description: structure.description,
          subject_id: structure.subject_id,
          grade_id: structure.grade_id
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update structure: ${response.status} - ${errorText}`);
      }

      console.log('✅ Structure updated successfully');
      setShowEditModal(false);
      setEditingStructure(null);
      await fetchInitialData(); // Refresh the list
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(`Failed to update structure: ${errorMessage}`);
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteStructure = async (structure: ContentStructure) => {
    setLoading(true);
    try {
      const response = await fetch(`${apiUrls.contentLibrary.contentStructures}/${structure.id}`, {
        method: 'DELETE',
        headers: getHttpHeaders()
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to delete structure: ${response.status} - ${errorText}`);
      }

      console.log('✅ Structure deleted successfully');
      setShowDeleteModal(false);
      setDeletingStructure(null);
      await fetchInitialData(); // Refresh the list
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(`Failed to delete structure: ${errorMessage}`);
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Function to fetch linked assets for a lesson
  const fetchLinkedAssets = async (lessonId: number) => {
    try {
      console.log(`🔍 Fetching linked assets for lesson ${lessonId}...`);
      const response = await fetch(apiUrls.contentLibrary.structureContentLinks(lessonId), {
        headers: getHttpHeaders()
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log(`✅ Linked assets for lesson ${lessonId}:`, result);
        
        if (result.success && result.data) {
          // Fetch full asset details for each linked asset
          const assetDetails = await Promise.all(
            result.data.map(async (link: any) => {
              try {
                const assetResponse = await fetch(apiUrls.contentAssets.byId(link.content_asset_id), {
                  headers: getHttpHeaders()
                });
                if (assetResponse.ok) {
                  const assetResult = await assetResponse.json();
                  return {
                    ...link,
                    asset: assetResult.success ? assetResult.content : null
                  };
                }
              } catch (err) {
                console.warn(`Failed to fetch asset details for ${link.content_asset_id}:`, err);
              }
              return link;
            })
          );
          
          setLinkedAssets(prev => ({
            ...prev,
            [lessonId]: assetDetails
          }));
        }
      }
    } catch (error) {
      console.warn(`Failed to fetch linked assets for lesson ${lessonId}:`, error);
    }
  };

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      console.log('🔍 API URLs being called:');
      console.log('🔍 Content Structures:', apiUrls.contentLibrary.contentStructures);
      console.log('🔍 Content Structures Full URL:', `${API_CONFIG.videoService}/api/content-structures`);
      console.log('🔍 Subjects:', apiUrls.books.subjects);
      console.log('🔍 Grades:', apiUrls.books.grades);
      console.log('🔍 Content Assets:', apiUrls.contentAssets.list);
      console.log('🔍 Books:', apiUrls.books.list);
      console.log('🔍 Book Service URL:', API_CONFIG.bookService);
      console.log('🔍 Video Service URL:', API_CONFIG.videoService);



      const [structuresData, subjectsData, gradesData, assetsData, booksData] = await Promise.all([
        fetch(apiUrls.contentLibrary.contentStructures)
          .then(r => {
            console.log('🔍 Content Structures response status:', r.status, r.statusText);
            if (!r.ok) {
              console.warn('⚠️ Content Structures API returned status:', r.status, r.statusText);
              return { structures: [] };
            }
            return r.json();
          })
          .catch(err => {
            console.warn('⚠️ Failed to fetch content structures:', err.message);
            return { structures: [] };
          }),
        fetch(apiUrls.books.subjects, { headers: getHttpHeaders() })
          .then(r => {
            if (!r.ok) {
              console.warn('⚠️ Subjects API returned status:', r.status, r.statusText);
              return { subjects: [] };
            }
            return r.json();
          })
          .catch(err => {
            console.warn('⚠️ Failed to fetch subjects:', err.message);
            return { subjects: [] };
          }),
        fetch(apiUrls.books.grades, { headers: getHttpHeaders() })
          .then(r => {
            if (!r.ok) {
              console.warn('⚠️ Grades API returned status:', r.status, r.statusText);
              return { grades: [] };
            }
            return r.json();
          })
          .catch(err => {
            console.warn('⚠️ Failed to fetch grades:', err.message);
            return { grades: [] };
          }),
        fetch(apiUrls.contentAssets.list, { headers: getHttpHeaders() })
          .then(r => {
            if (!r.ok) {
              console.warn('⚠️ Content Assets API returned status:', r.status, r.statusText);
              return { content: [] };
            }
            return r.json();
          })
          .catch(err => {
            console.warn('⚠️ Failed to fetch content assets:', err.message);
            return { content: [] };
          }),
        fetch(apiUrls.books.list, { headers: getHttpHeaders() })
          .then(r => {
            if (!r.ok) {
              console.warn('⚠️ Books API returned status:', r.status, r.statusText);
              return { books: [] };
            }
            return r.json();
          })
          .catch(err => {
            console.warn('⚠️ Failed to fetch books:', err.message);
            return { books: [] };
          })
      ]);

      const structuresList = structuresData.structures || [];
      setStructures(structuresList);
      
      // Build tree from flat structures
      const tree = buildTree(structuresList);
      setTreeData(tree);
      
      setSubjects(subjectsData.subjects || []);
      setFilteredSubjects(subjectsData.subjects || []); // Initialize with all subjects
      setGrades(gradesData.grades || []);
      setContentAssets(assetsData.content || []);
      
      // Fetch linked assets for all lessons
      // Lessons are structures that have a parent but no children (leaf nodes)
      const lessons = structuresList.filter((s: any) => {
        const hasParent = s.parent_id !== null;
        const hasChildren = structuresList.some((child: any) => child.parent_id === s.id);
        return hasParent && !hasChildren;
      });
      console.log(`🔍 Found ${lessons.length} lessons, fetching linked assets...`);
      console.log('📋 Lesson IDs:', lessons.map((l: any) => `${l.id}(${l.name})`));
      lessons.forEach((lesson: any) => {
        fetchLinkedAssets(lesson.id);
      });
      setBooks(booksData.books || []);
    } catch (err) {
      setError('Failed to fetch initial data');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle grade selection and filter subjects
  const handleGradeChange = async (gradeId: string) => {
    // Update wizard data
    updateWizardData('grade_id', gradeId);
    
    if (!gradeId || gradeId === '') {
      // If no grade is selected, show all subjects
      setFilteredSubjects(subjects);
    } else {
      try {
        // Fetch subjects filtered by the selected grade
        const response = await fetch(`${apiUrls.books.subjects}/by-grade/${gradeId}`);
        if (response.ok) {
          const data = await response.json();
          setFilteredSubjects(data.subjects || []);
          console.log(`✅ Loaded ${data.subjects?.length || 0} subjects for grade ${gradeId}`);
        } else {
          console.error('Failed to fetch subjects for grade:', gradeId);
          setFilteredSubjects([]);
        }
      } catch (error) {
        console.error('Error fetching subjects for grade:', error);
        setFilteredSubjects([]);
      }
    }
    
    // Reset subject selection when grade changes
    updateWizardData('subject_id', '');
  };

  // Handle main filter grade change (for toolbar filters)
  const handleMainGradeChange = async (gradeId: string) => {
    setFilterGrade(gradeId);
    
    if (!gradeId || gradeId === 'all') {
      // If "All Grades" is selected, show all subjects
      setFilteredSubjects(subjects);
      setFilterSubject('all'); // Reset subject filter
    } else {
      try {
        // Fetch subjects filtered by the selected grade
        const response = await fetch(`${apiUrls.books.subjects}/by-grade/${gradeId}`);
        if (response.ok) {
          const data = await response.json();
          const gradeSubjects = data.subjects || [];
          setFilteredSubjects(gradeSubjects);
          console.log(`✅ Loaded ${gradeSubjects.length} subjects for grade ${gradeId}`);
          
          // Reset subject filter to "all" when grade changes
          setFilterSubject('all');
        } else {
          console.error('Failed to fetch subjects for grade:', gradeId);
          setFilteredSubjects([]);
          setFilterSubject('all');
        }
      } catch (error) {
        console.error('Error fetching subjects for grade:', error);
        setFilteredSubjects([]);
        setFilterSubject('all');
      }
    }
  };

  // Enhanced Progressive Creation Wizard
  const startWizard = () => {
    setShowWizard(true);
    setCurrentStep(1);
    setWizardData({
      name: '',
      description: '',
      subject_id: '',
      grade_id: '',
      sort_order: 0,
      is_active: true,
      topics: []
    });
  };

  const nextStep = () => {
    if (currentStep < wizardSteps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const updateWizardData = (field: string, value: any) => {
    setWizardData(prev => ({ ...prev, [field]: value }));
  };

  // Helper functions for managing hierarchy
  const addTopic = () => {
    const newTopic = {
      id: `topic_${Date.now()}`,
      name: '',
      description: '',
      chapters: []
    };
    setWizardData(prev => ({
      ...prev,
      topics: [...prev.topics, newTopic]
    }));
  };

  const updateTopic = (topicId: string, field: string, value: string) => {
    setWizardData(prev => ({
      ...prev,
      topics: prev.topics.map(topic =>
        topic.id === topicId ? { ...topic, [field]: value } : topic
      )
    }));
  };

  const addChapter = (topicId: string) => {
    const newChapter = {
      id: `chapter_${Date.now()}`,
      name: '',
      description: '',
      units: []
    };
    setWizardData(prev => ({
      ...prev,
      topics: prev.topics.map(topic =>
        topic.id === topicId
          ? { ...topic, chapters: [...topic.chapters, newChapter] }
          : topic
      )
    }));
  };

  const updateChapter = (topicId: string, chapterId: string, field: string, value: string) => {
    setWizardData(prev => ({
      ...prev,
      topics: prev.topics.map(topic =>
        topic.id === topicId
          ? {
            ...topic,
            chapters: topic.chapters.map(chapter =>
              chapter.id === chapterId ? { ...chapter, [field]: value } : chapter
            )
          }
          : topic
      )
    }));
  };

  const removeChapter = (topicId: string, chapterId: string) => {
    setWizardData(prev => ({
      ...prev,
      topics: prev.topics.map(topic =>
        topic.id === topicId
          ? {
            ...topic,
            chapters: topic.chapters.filter(chapter => chapter.id !== chapterId)
          }
          : topic
      )
    }));
  };

  const removeTopic = (topicId: string) => {
    setWizardData(prev => ({
      ...prev,
      topics: prev.topics.filter(topic => topic.id !== topicId)
    }));
  };

  const removeUnit = (topicId: string, chapterId: string, unitId: string) => {
    setWizardData(prev => ({
      ...prev,
      topics: prev.topics.map(topic =>
        topic.id === topicId
          ? {
            ...topic,
            chapters: topic.chapters.map(chapter =>
              chapter.id === chapterId
                ? {
                  ...chapter,
                  units: chapter.units.filter(unit => unit.id !== unitId)
                }
                : chapter
            )
          }
          : topic
      )
    }));
  };

  const addLesson = (topicId: string, chapterId: string, unitId: string) => {
    const newLesson = {
      id: `lesson_${Date.now()}`,
      name: '',
      description: '',
      content_asset_ids: []
    };
    setWizardData(prev => ({
      ...prev,
      topics: prev.topics.map(topic =>
        topic.id === topicId
          ? {
            ...topic,
            chapters: topic.chapters.map(chapter =>
              chapter.id === chapterId
                ? {
                  ...chapter,
                  units: chapter.units.map(unit =>
                    unit.id === unitId
                      ? { ...unit, lessons: [...unit.lessons, newLesson] }
                      : unit
                  )
                }
                : chapter
            )
          }
          : topic
      )
    }));
  };

  const updateLesson = (topicId: string, chapterId: string, unitId: string, lessonId: string, field: string, value: string | number[]) => {
    setWizardData(prev => ({
      ...prev,
      topics: prev.topics.map(topic =>
        topic.id === topicId
          ? {
            ...topic,
            chapters: topic.chapters.map(chapter =>
              chapter.id === chapterId
                ? {
                  ...chapter,
                  units: chapter.units.map(unit =>
                    unit.id === unitId
                      ? {
                        ...unit,
                        lessons: unit.lessons.map(lesson =>
                          lesson.id === lessonId ? { ...lesson, [field]: value } : lesson
                        )
                      }
                      : unit
                  )
                }
                : chapter
            )
          }
          : topic
      )
    }));
  };

  const removeLesson = (topicId: string, chapterId: string, unitId: string, lessonId: string) => {
    setWizardData(prev => ({
      ...prev,
      topics: prev.topics.map(topic =>
        topic.id === topicId
          ? {
            ...topic,
            chapters: topic.chapters.map(chapter =>
              chapter.id === chapterId
                ? {
                  ...chapter,
                  units: chapter.units.map(unit =>
                    unit.id === unitId
                      ? {
                        ...unit,
                        lessons: unit.lessons.filter(lesson => lesson.id !== lessonId)
                      }
                      : unit
                  )
                }
                : chapter
            )
          }
          : topic
      )
    }));
  };

  const addUnit = (topicId: string, chapterId: string) => {
    const newUnit = {
      id: `unit_${Date.now()}`,
      name: '',
      description: '',
      lessons: []
    };
    setWizardData(prev => ({
      ...prev,
      topics: prev.topics.map(topic =>
        topic.id === topicId
          ? {
            ...topic,
            chapters: topic.chapters.map(chapter =>
              chapter.id === chapterId
                ? { ...chapter, units: [...chapter.units, newUnit] }
                : chapter
            )
          }
          : topic
      )
    }));
  };

  const updateUnit = (topicId: string, chapterId: string, unitId: string, field: string, value: string) => {
    setWizardData(prev => ({
      ...prev,
      topics: prev.topics.map(topic =>
        topic.id === topicId
          ? {
            ...topic,
            chapters: topic.chapters.map(chapter =>
              chapter.id === chapterId
                ? {
                  ...chapter,
                  units: chapter.units.map(unit =>
                    unit.id === unitId ? { ...unit, [field]: value } : unit
                  )
                }
                : chapter
            )
          }
          : topic
      )
    }));
  };



  const validateCurrentStep = (): boolean => {
    switch (currentStep) {
      case 1:
        return wizardData.name.trim().length > 0 && !!wizardData.subject_id && !!wizardData.grade_id;
      case 2:
        return wizardData.topics.length > 0 && wizardData.topics.every(topic =>
          topic.name.trim().length > 0
        );
      case 3:
        return wizardData.topics.every(topic =>
          topic.chapters.length > 0 && topic.chapters.every(chapter =>
            chapter.name.trim().length > 0
          )
        );
      case 4:
        return wizardData.topics.every(topic =>
          topic.chapters.every(chapter =>
            chapter.units.length > 0 && chapter.units.every(unit =>
              unit.name.trim().length > 0
            )
          )
        );
      case 5:
        return wizardData.topics.every(topic =>
          topic.chapters.every(chapter =>
            chapter.units.every(unit =>
              unit.lessons.length > 0 && unit.lessons.every(lesson =>
                lesson.name.trim().length > 0
              )
            )
          )
        );
      case 6:
        return true; // Final validation
      default:
        return false;
    }
  };

  // Helper function to safely extract structure ID from API response
  const extractStructureId = (response: any): number => {
    console.log('🔍 Extracting ID from response:', response);
    
    // Try different possible response formats
    if (response.structure && response.structure.id) {
      console.log('✅ Found ID in response.structure.id:', response.structure.id);
      return response.structure.id;
    }
    if (response.data && response.data.id) {
      console.log('✅ Found ID in response.data.id:', response.data.id);
      return response.data.id;
    }
    if (response.id) {
      console.log('✅ Found ID in response.id:', response.id);
      return response.id;
    }
    if (response.content_structure && response.content_structure.id) {
      console.log('✅ Found ID in response.content_structure.id:', response.content_structure.id);
      return response.content_structure.id;
    }
    if (response.insertId) {
      console.log('✅ Found ID in response.insertId:', response.insertId);
      return response.insertId;
    }
    if (response.success && response.structure_id) {
      console.log('✅ Found ID in response.structure_id:', response.structure_id);
      return response.structure_id;
    }
    
    console.error('❌ Unexpected API response format:', response);
    console.error('❌ Response keys:', Object.keys(response));
    console.error('❌ Full response:', JSON.stringify(response));
    throw new Error(`Invalid API response format - no structure ID found. Response keys: ${Object.keys(response).join(', ')}`);
  };

  const submitWizard = async () => {
    if (!validateCurrentStep()) return;

    setLoading(true);
    try {
      // Step 1: Create top-level structure (the root)
      const rootPayload = {
        name: wizardData.name?.trim() || 'Untitled',
        description: wizardData.description?.trim() || '',
        subject_id: Number(wizardData.subject_id) || null,
        grade_id: Number(wizardData.grade_id) || null,
        parent_id: null,
        sort_order: Number(wizardData.sort_order) || 0,
        is_active: Number(wizardData.is_active) ?? true
      };

      console.log("🟢 Creating root structure:", rootPayload);

      const structureResponse = await fetch(apiUrls.contentLibrary.contentStructures, {
        method: 'POST',
        headers: { ...getHttpHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(rootPayload)
      });

      if (!structureResponse.ok) {
        const text = await structureResponse.text();
        throw new Error(`Failed to create root structure: ${structureResponse.status} ${text}`);
      }

      const structureResult = await structureResponse.json();
      const rootId = extractStructureId(structureResult);

      // Step 2: Create nested structures recursively
      console.log(`🔄 Starting creation of ${wizardData.topics.length} topics...`);
      for (let tIndex = 0; tIndex < wizardData.topics.length; tIndex++) {
        const topic = wizardData.topics[tIndex];
        const topicPayload = {
          name: topic.name?.trim() || 'Untitled Topic',
          description: topic.description?.trim() || '',
          subject_id: wizardData.subject_id || null,
          grade_id: wizardData.grade_id || null,
          parent_id: rootId,
          sort_order: tIndex + 1,
          is_active: true
        };
        console.log("📘 Creating topic:", topicPayload);

        const topicRes = await fetch(apiUrls.contentLibrary.contentStructures, {
          method: 'POST',
          headers: { ...getHttpHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify(topicPayload)
        });

        if (!topicRes.ok) {
          const text = await topicRes.text();
          console.warn(`❌ Failed to create topic [${topic.name}]:`, text);
          continue;
        }

        const topicResult = await topicRes.json();
        const topicId = extractStructureId(topicResult);

        // Create chapters for this topic
        console.log(`📚 Creating ${topic.chapters.length} chapters for topic [${topic.name}]...`);
        for (let chapterIndex = 0; chapterIndex < topic.chapters.length; chapterIndex++) {
          const chapter = topic.chapters[chapterIndex];

          const chapterResponse = await fetch(apiUrls.contentLibrary.contentStructures, {
            method: 'POST',
            headers: { ...getHttpHeaders(), 'Content-Type': 'application/json' },
            body: JSON.stringify({
              name: chapter.name,
              description: chapter.description,
              subject_id: wizardData.subject_id,
              grade_id: wizardData.grade_id,
              parent_id: topicId,
              sort_order: chapterIndex + 1,
              is_active: true
            })
          });

          if (!chapterResponse.ok) {
            const text = await chapterResponse.text();
            console.warn(`❌ Failed to create chapter [${chapter.name}]:`, text);
            continue;
          }
          const chapterResult = await chapterResponse.json();
          const chapterId = extractStructureId(chapterResult);

          // Create units for this chapter
          console.log(`📖 Creating ${chapter.units.length} units for chapter [${chapter.name}]...`);
          for (let unitIndex = 0; unitIndex < chapter.units.length; unitIndex++) {
            const unit = chapter.units[unitIndex];

            const unitResponse = await fetch(apiUrls.contentLibrary.contentStructures, {
              method: 'POST',
              headers: { ...getHttpHeaders(), 'Content-Type': 'application/json' },
              body: JSON.stringify({
                name: unit.name,
                description: unit.description,
                subject_id: wizardData.subject_id,
                grade_id: wizardData.grade_id,
                parent_id: chapterId,
                sort_order: unitIndex + 1,
                is_active: true
              })
            });

            if (!unitResponse.ok) {
              const text = await unitResponse.text();
              console.warn(`❌ Failed to create unit [${unit.name}]:`, text);
              continue;
            }
            const unitResult = await unitResponse.json();
            const unitId = extractStructureId(unitResult);

            // Create lessons for this unit
            console.log(`📝 Creating ${unit.lessons.length} lessons for unit [${unit.name}]...`);
            for (let lessonIndex = 0; lessonIndex < unit.lessons.length; lessonIndex++) {
              const lesson = unit.lessons[lessonIndex];

              const lessonResponse = await fetch(apiUrls.contentLibrary.contentStructures, {
                method: 'POST',
                headers: { ...getHttpHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  name: lesson.name,
                  description: lesson.description,
                  subject_id: wizardData.subject_id,
                  grade_id: wizardData.grade_id,
                  parent_id: unitId,
                  sort_order: lessonIndex + 1,
                  is_active: true
                })
              });

              if (!lessonResponse.ok) {
                const text = await lessonResponse.text();
                console.warn(`❌ Failed to create lesson [${lesson.name}]:`, text);
                continue;
              }
              const lessonResult = await lessonResponse.json();
              console.log(`📝 Lesson created, response:`, lessonResult);
              
              const lessonId = extractStructureId(lessonResult);
              console.log(`✅ Lesson ID extracted: ${lessonId} for lesson: ${lesson.name}`);

              // Link content assets to this lesson
              if (lesson.content_asset_ids && lesson.content_asset_ids.length > 0) {
                console.log(`📎 Linking ${lesson.content_asset_ids.length} assets to lesson ${lessonId} (${lesson.name})`);
                
                for (const assetId of lesson.content_asset_ids) {
                  try {
                    console.log(`  → Linking asset ${assetId} to lesson ${lessonId}...`);
                    
                    const linkResponse = await fetch(apiUrls.contentLibrary.structureContentLinks(lessonId), {
                      method: 'POST',
                      headers: { ...getHttpHeaders(), 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        content_asset_id: assetId,
                        sort_order: 0
                      })
                    });
                    
                    if (linkResponse.ok) {
                      console.log(`  ✅ Asset ${assetId} linked successfully`);
                    } else {
                      const errorData = await linkResponse.json().catch(() => ({ error: 'Unknown' }));
                      console.error(`  ❌ Failed to link asset ${assetId}:`, errorData);
                      console.error(`  ❌ Response status: ${linkResponse.status}`);
                      console.error(`  ❌ Endpoint called: ${apiUrls.contentLibrary.structureContentLinks(lessonId)}`);
                    }
                  } catch (linkError) {
                    console.error(`  ❌ Error linking asset ${assetId}:`, linkError);
                  }
                }
                
                console.log(`✅ Finished linking assets to lesson ${lesson.name}`);
              } else {
                console.log(`ℹ️ No assets selected for lesson ${lesson.name}`);
              }
            }
          }
        }
      }

      console.log("✅ Content structure hierarchy created successfully!");
      setShowWizard(false);
      setCurrentStep(1);
      fetchInitialData();
      // Show success message
    } catch (err) {
      setError('Failed to create content structure hierarchy');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Drag & Drop Sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Drag & Drop Reordering
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id && over) {
      const activeStructure = structures.find(s => s.id === active.id);
      const overStructure = structures.find(s => s.id === over.id);

      if (activeStructure && overStructure) {
        const activeNode = treeData.find(n => n.id === activeStructure.id);
        const overNode = treeData.find(n => n.id === overStructure.id);
        if (activeNode && overNode && activeNode.level === overNode.level) {
        const oldIndex = structures.findIndex(s => s.id === active.id);
        const newIndex = structures.findIndex(s => s.id === over.id);

        const reordered = arrayMove(structures, oldIndex, newIndex);

        // Update sort orders for the affected level
        const updatedStructures = reordered.map((structure, index) => {
            const structureNode = treeData.find(n => n.id === structure.id);
            if (structureNode && structureNode.level === activeNode.level) {
            return { ...structure, sort_order: index };
          }
          return structure;
        });

        setStructures(updatedStructures);

        // Save new order to backend
        try {
          await fetch(`${apiUrls.contentLibrary.reorderContentStructures}`, {
            method: 'PUT',
            headers: { ...getHttpHeaders(), 'Content-Type': 'application/json' },
            body: JSON.stringify({
                level: activeNode.level, 
              structures: updatedStructures
                  .filter(s => {
                    const sNode = treeData.find(n => n.id === s.id);
                    return sNode && sNode.level === activeNode.level;
                  })
                .map((s, i) => ({ id: s.id, sort_order: i }))
            })
          });
        } catch (err) {
          console.error('Failed to save new order:', err);
          }
        }
      }
    }
  };

  // Tree View Navigation
  const toggleNode = (nodeId: number) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  // Asset Preview Handlers
  const handleAssetHover = (e: React.MouseEvent, asset: any) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const previewWidth = 400;
    const previewHeight = 300;
    const margin = 10;
    
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    let x = rect.right + margin; // Default: position to the right
    let y = rect.top;
    
    // If preview would go off right edge, position to the left
    if (x + previewWidth > viewportWidth - margin) {
      x = rect.left - previewWidth - margin;
    }
    
    // If still off left edge, clamp to right edge
    if (x < margin) {
      x = viewportWidth - previewWidth - margin;
    }
    
    // Adjust Y position if preview would go off bottom edge
    if (y + previewHeight > viewportHeight - margin) {
      y = viewportHeight - previewHeight - margin;
    }
    
    // Ensure preview doesn't go off top edge
    y = Math.max(margin, y);
    
    setAssetPreview({
      visible: true,
      asset: asset,
      x: x,
      y: y
    });
  };

  const handleAssetLeave = () => {
    setAssetPreview({
      visible: false,
      asset: null,
      x: 0,
      y: 0
    });
  };

  // Context Menu Handlers
  const handleContextMenu = (e: React.MouseEvent, item: CSNode) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Calculate smart positioning to avoid screen edges
    const menuWidth = 180; // Approximate menu width
    const menuHeight = 200; // Approximate menu height (varies by content)
    const margin = 10; // Margin from screen edges
    
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    let x = e.clientX;
    let y = e.clientY;
    
    // Smart X positioning
    if (x + menuWidth > viewportWidth - margin) {
      // Try to position to the left of cursor
      x = e.clientX - menuWidth;
      // If still off-screen, clamp to right edge
      if (x < margin) {
        x = viewportWidth - menuWidth - margin;
      }
    }
    
    // Smart Y positioning
    if (y + menuHeight > viewportHeight - margin) {
      // Try to position above cursor
      y = e.clientY - menuHeight;
      // If still off-screen, clamp to bottom edge
      if (y < margin) {
        y = viewportHeight - menuHeight - margin;
      }
    }
    
    // Final bounds check
    x = Math.max(margin, Math.min(x, viewportWidth - menuWidth - margin));
    y = Math.max(margin, Math.min(y, viewportHeight - menuHeight - margin));
    
    setContextMenu({
      visible: true,
      x: x,
      y: y,
      item: item
    });
  };

  const handleContextMenuAction = (action: string) => {
    if (!contextMenu.item) return;

    switch (action) {
      case 'edit':
        // Check if this is a root structure (no parent_id)
        if (!contextMenu.item.parent_id) {
          // Use modal for root structure editing (includes grade selection)
          const rootStructure = treeData.find(item => item.id === contextMenu.item!.id);
          setEditingStructureData({
            id: contextMenu.item.id,
            name: contextMenu.item.name,
            grade_id: rootStructure?.grade_id || contextMenu.item.grade_id || 0,
            subject_id: rootStructure?.subject_id || contextMenu.item.subject_id || 0
          });
          // Load subjects for the current grade
          if (rootStructure?.grade_id) {
            handleEditModalGradeChange(rootStructure.grade_id);
          }
          setStructureEditModalOpen(true);
        } else {
          // Use inline edit for child structures (name only)
          setEditingItem({
            id: contextMenu.item.id,
            name: contextMenu.item.name
          });
        }
        break;
      case 'delete':
        handleDeleteStructure(contextMenu.item as any);
        break;
      case 'assets':
        setSelectedLessonForAssets(contextMenu.item);
        setAssetModalOpen(true);
        // Fetch all available assets - admin can link any asset to any lesson
        fetchAvailableAssets();
        break;
      case 'manage_books':
        if (!contextMenu.item.parent_id) { // Root structure only
          setSelectedStructureForBooks(contextMenu.item);
          setBookContentModalOpen(true);
        }
        break;
      case 'add_topic':
        handleAddChild(contextMenu.item, 'topic');
        break;
      case 'add_chapter':
        handleAddChild(contextMenu.item, 'chapter');
        break;
      case 'add_unit':
        handleAddChild(contextMenu.item, 'unit');
        break;
      case 'add_lesson':
        handleAddChild(contextMenu.item, 'lesson');
        break;
      case 'clone':
        handleCloneStructure(contextMenu.item);
        break;
    }
    
    setContextMenu(prev => ({ ...prev, visible: false }));
  };

  // Inline Edit Handlers
  const handleInlineEditSave = async (newName: string, newGradeId?: number) => {
    if (!editingItem || !newName.trim()) return;

    try {
      const updateData: any = {
        name: newName.trim()
      };

      // If this is a root structure and grade is provided, include it
      if (newGradeId !== undefined && !contextMenu.item?.parent_id) {
        updateData.grade_id = newGradeId;
      }

      const response = await fetch(`${apiUrls.contentLibrary.contentStructures}/${editingItem.id}`, {
        method: 'PUT',
        headers: { ...getHttpHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        console.log('✅ Structure updated successfully');
        fetchInitialData(); // Refresh the tree
      } else {
        console.error('❌ Failed to update structure');
      }
    } catch (error) {
      console.error('❌ Error updating structure:', error);
    }

    setEditingItem(null);
  };

  const handleInlineEditCancel = () => {
    setEditingItem(null);
  };

  // Structure Edit Modal Handlers
  const handleEditModalGradeChange = async (gradeId: number) => {
    try {
      const response = await fetch(`${apiUrls.books.subjects}/by-grade/${gradeId}`);
      if (response.ok) {
        const data = await response.json();
        setEditModalFilteredSubjects(data.subjects || []);
        console.log(`✅ Loaded ${data.subjects?.length || 0} subjects for grade ${gradeId}`);
      } else {
        console.error('Failed to fetch subjects for grade:', gradeId);
        setEditModalFilteredSubjects([]);
      }
    } catch (error) {
      console.error('Error fetching subjects for grade:', error);
      setEditModalFilteredSubjects([]);
    }
  };

  const handleStructureEditSave = async () => {
    if (!editingStructureData || !editingStructureData.name.trim()) return;

    try {
      const response = await fetch(`${apiUrls.contentLibrary.contentStructures}/${editingStructureData.id}`, {
        method: 'PUT',
        headers: { ...getHttpHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: editingStructureData.name.trim(),
          grade_id: editingStructureData.grade_id,
          subject_id: editingStructureData.subject_id
        })
      });

      if (response.ok) {
        console.log('✅ Structure updated successfully');
        fetchInitialData(); // Refresh the tree
        setStructureEditModalOpen(false);
        setEditingStructureData(null);
        setEditModalFilteredSubjects([]);
        alert('Structure updated successfully!');
      } else {
        const errorData = await response.json();
        console.error('❌ Failed to update structure:', errorData);
        alert(`Failed to update structure: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('❌ Error updating structure:', error);
      alert(`Error updating structure: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleStructureEditCancel = () => {
    setStructureEditModalOpen(false);
    setEditingStructureData(null);
    setEditModalFilteredSubjects([]);
  };

  // Clone Structure Handler
  const handleCloneStructure = async (structure: any) => {
    if (!structure || structure.parent_id) return; // Only allow cloning root structures

    try {
      console.log('🔄 Cloning structure:', structure);
      
      // Create the cloned structure with "Clone" appended to the name
      const clonedName = `${structure.name} Clone`;
      
      const response = await fetch(`${apiUrls.contentLibrary.contentStructures}/${structure.id}/clone`, {
        method: 'POST',
        headers: { ...getHttpHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: clonedName
        })
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Structure cloned successfully:', result);
        fetchInitialData(); // Refresh the tree to show the cloned structure
        alert(`Structure "${structure.name}" has been cloned as "${clonedName}"`);
      } else {
        const errorData = await response.json();
        console.error('❌ Failed to clone structure:', errorData);
        alert(`Failed to clone structure: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('❌ Error cloning structure:', error);
      alert(`Error cloning structure: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  // Asset Management Functions
  const fetchAvailableAssets = async () => {
    setLoadingAssets(true);
    try {
      console.log(`🔍 Fetching all available content assets...`);
      // Fetch ALL assets - don't filter by grade/subject
      const response = await fetch(apiUrls.contentAssets.list, {
        headers: getHttpHeaders()
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Available assets fetched:', result);
        
        const assets = result.success ? result.content : (result.content || result || []);
        console.log(`✅ Loaded ${assets.length} total assets`);
        setAvailableAssets(assets);
      } else {
        console.error('❌ Failed to fetch available assets');
        setAvailableAssets([]);
      }
    } catch (error) {
      console.error('❌ Error fetching available assets:', error);
      setAvailableAssets([]);
    }
    setLoadingAssets(false);
  };

  const handleAddAssetToLesson = async (assetId: number) => {
    if (!selectedLessonForAssets) return;

    try {
      console.log(`🔗 Linking asset ${assetId} to lesson ${selectedLessonForAssets.id}...`);
      const response = await fetch(apiUrls.contentLibrary.structureContentLinks(selectedLessonForAssets.id), {
        method: 'POST',
        headers: { ...getHttpHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          structure_id: selectedLessonForAssets.id,
          content_asset_id: assetId
        })
      });

      if (response.ok) {
        console.log('✅ Asset linked successfully');
        // Refresh linked assets for this lesson
        await fetchLinkedAssets(selectedLessonForAssets.id);
        // Refresh available assets to update the list
        await fetchAvailableAssets();
      } else {
        console.error('❌ Failed to link asset');
        alert('Failed to link asset. Please try again.');
      }
    } catch (error) {
      console.error('❌ Error linking asset:', error);
      alert('Error linking asset. Please try again.');
    }
  };

  const handleRemoveAssetFromLesson = async (linkId: number) => {
    if (!selectedLessonForAssets) return;

    try {
      console.log(`🗑️ Removing asset link ${linkId} from lesson ${selectedLessonForAssets.id}...`);
      const response = await fetch(`${apiUrls.contentLibrary.structureContentLinks(selectedLessonForAssets.id)}/${linkId}`, {
        method: 'DELETE',
        headers: getHttpHeaders()
      });

      if (response.ok) {
        console.log('✅ Asset unlinked successfully');
        // Refresh linked assets for this lesson
        await fetchLinkedAssets(selectedLessonForAssets.id);
        // Refresh available assets to update the list
        await fetchAvailableAssets();
      } else {
        console.error('❌ Failed to unlink asset');
        alert('Failed to remove asset. Please try again.');
      }
    } catch (error) {
      console.error('❌ Error removing asset:', error);
      alert('Error removing asset. Please try again.');
    }
  };

  // Helper function to find node by ID in tree
  const findNodeById = (id: number): (node: CSNode) => boolean => {
    return (node: CSNode) => {
      if (node.id === id) return true;
      if (node.children) {
        return node.children.some(findNodeById(id));
      }
      return false;
    };
  };



  // Add Child Functions
  const handleAddChild = (parentItem: CSNode | null, childType: 'topic' | 'chapter' | 'unit' | 'lesson') => {
    console.log('🔧 handleAddChild called with:');
    console.log('🔧 parentItem:', parentItem);
    console.log('🔧 parentItem?.id:', parentItem?.id);
    console.log('🔧 childType:', childType);
    
    setAddChildData({
      parentItem,
      childType,
      childName: ''
    });
    setAddChildModalOpen(true);
  };



  const handleAddTopicToStructure = () => {
    console.log('🚀 handleAddTopicToStructure called');
    console.log('🔍 All tree data:', treeData);
    console.log('🔍 Tree data length:', treeData.length);
    
    // Let's check each item's parent_id value specifically FIRST
    treeData.forEach(item => {
      console.log(`🔍 Item ${item.id} (${item.name}): parent_id = ${item.parent_id} (type: ${typeof item.parent_id})`);
    });
    
    // Find root structures (parent_id = null) to choose from
    // Check both null and undefined values for parent_id
    const rootStructures = treeData.filter(item => {
      const isRoot = !item.parent_id || item.parent_id === null;
      console.log(`🔍 Item ${item.id}: isRoot = ${isRoot} (parent_id: ${item.parent_id})`);
      return isRoot;
    });
    
    console.log('🔍 Root structures found:', rootStructures);
    console.log('🔍 Root structures count:', rootStructures.length);
    console.log('🔍 Looking for record with ID 64:', treeData.find(item => item.id === 64));
    
    if (rootStructures.length === 0) {
      console.log('❌ No root structures found!');
      alert('No root structures found. Please create a structure first using the wizard.');
      return;
    }
    
    if (rootStructures.length === 1) {
      // If only one root structure, use it directly
      console.log('✅ Using single root structure as parent:', rootStructures[0]);
      console.log('🔍 Root structure parent_id:', rootStructures[0].parent_id);
      console.log('🔍 Root structure ID:', rootStructures[0].id);
      handleAddChild(rootStructures[0], 'topic');
    } else {
      // Multiple structures - show selection
      console.log('⚠️ Multiple root structures found, showing selection');
      setSelectedStructure(null);
      // We'll show a structure selection modal
      alert('Please select a structure first, then right-click on it to add a topic.');
    }
  };

  const handleCreateChild = async () => {
    if (!addChildData.childName.trim()) {
      alert('Please enter a name for the new ' + addChildData.childType);
      return;
    }

    try {
      console.log(`🔨 Creating new ${addChildData.childType}:`, addChildData.childName);
      console.log('🔍 Current tree data:', treeData);
      console.log('🔍 Add child data:', addChildData);
      console.log('🔍 Parent item from addChildData:', addChildData.parentItem);
      console.log('🔍 Parent item ID:', addChildData.parentItem?.id);
      
      // Get parent information
      const parentId = addChildData.parentItem?.id || null;
      console.log('🔍 Final parentId to be used:', parentId);
      
      // For root-level topics, we need to provide default grade and subject
      // Try to get from parent, then from existing tree data, then use defaults
      let gradeId = addChildData.parentItem?.grade_id;
      let subjectId = addChildData.parentItem?.subject_id;
      
      if (!gradeId || !subjectId) {
        // Try to get from first item in tree data
        const firstItem = treeData[0];
        gradeId = gradeId || firstItem?.grade_id || 3; // Default to grade 3
        subjectId = subjectId || firstItem?.subject_id || 1; // Default to subject 1
      }

      console.log('🔍 Resolved IDs - Parent:', parentId, 'Grade:', gradeId, 'Subject:', subjectId);

      const payload = {
        name: addChildData.childName.trim(),
        parent_id: parentId,
        grade_id: gradeId,
        subject_id: subjectId,
        sort_order: 1 // Will be adjusted by backend
      };

      console.log('📤 Creating structure with payload:', payload);
      console.log('📤 API URL:', apiUrls.contentLibrary.contentStructures);

      const response = await fetch(apiUrls.contentLibrary.contentStructures, {
        method: 'POST',
        headers: { ...getHttpHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      console.log('📥 Response status:', response.status);
      console.log('📥 Response headers:', Object.fromEntries(response.headers.entries()));

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Structure created successfully:', result);
        
        // Close modal and refresh data
        setAddChildModalOpen(false);
        setAddChildData({
          parentItem: null,
          childType: 'topic',
          childName: ''
        });
        
        console.log('🔄 Refreshing tree data...');
        // Refresh the tree data
        await fetchInitialData();
        
        // Expand the parent node to show the new child
        if (parentId) {
          console.log('📂 Expanding parent node:', parentId);
          setExpandedNodes(prev => {
            const newSet = new Set(prev);
            newSet.add(parentId);
            return newSet;
          });
        }
        
        console.log('✅ Create operation completed successfully');
      } else {
        console.error('❌ Failed to create structure - Status:', response.status);
        const responseText = await response.text();
        console.error('❌ Response body:', responseText);
        
        let errorData = {};
        try {
          errorData = JSON.parse(responseText);
        } catch (e) {
          console.error('❌ Could not parse error response as JSON');
        }
        
        alert(`Failed to create ${addChildData.childType}. Status: ${response.status}. ${(errorData as any).message || responseText || 'Please try again.'}`);
      }
    } catch (error) {
      console.error('❌ Error creating structure:', error);
      alert(`Error creating ${addChildData.childType}: ${(error as any).message}. Please try again.`);
    }
  };

  const getChildTypeForParent = (item: CSNode): 'topic' | 'chapter' | 'unit' | 'lesson' | null => {
    // Check if this is a root structure (parent_id = null)
    if (!item.parent_id) {
      return 'topic';  // Root structure -> Topic
    }
    
    // For other items, determine by level in hierarchy
    const level = item.level || 0;
    switch (level) {
      case 1: return 'chapter';  // Topic -> Chapter
      case 2: return 'unit';     // Chapter -> Unit
      case 3: return 'lesson';   // Unit -> Lesson
      default: return null;      // Lesson has no children
    }
  };

  const renderTreeView = (items: CSNode[], level: number = 0) => {
    return items.map((item) => (
      <div key={item.id} style={{ marginLeft: level * 8 }}>
        <div 
          style={{ 
          display: 'flex',
          alignItems: 'center',
            padding: '6px 8px',
            backgroundColor: level % 2 === 0 ? '#fafafa' : '#ffffff',
            border: 'none',
            borderLeft: level > 0 ? '2px solid #e5e7eb' : 'none',
            borderRadius: '3px',
            marginBottom: '2px',
            transition: 'background-color 0.2s ease',
            cursor: 'pointer'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f3f4f6';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = level % 2 === 0 ? '#fafafa' : '#ffffff';
          }}
          onContextMenu={(e) => handleContextMenu(e, item)}
        >
          <button
            onClick={() => toggleNode(item.id)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              marginRight: '8px'
            }}
          >
            {item.children && item.children.length > 0 ? (
              expandedNodes.has(item.id) ? <FiChevronDown /> : <FiChevronRight />
            ) : (
              <div style={{ width: '16px', height: '16px' }} />
            )}
          </button>

          <div style={{
            width: '16px',
            height: '16px',
            borderRadius: '50%',
            backgroundColor: getLevelColor(item.level),
            marginRight: '12px'
          }} />

          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px' }}>
            {/* Inline Edit or Display Name */}
            {editingItem && editingItem.id === item.id ? (
              <input
                type="text"
                defaultValue={editingItem.name}
                autoFocus
                onBlur={(e) => handleInlineEditSave(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleInlineEditSave((e.target as HTMLInputElement).value);
                  } else if (e.key === 'Escape') {
                    handleInlineEditCancel();
                  }
                }}
                onClick={(e) => e.stopPropagation()}
              style={{
                  background: 'white',
                  border: '2px solid #3b82f6',
                borderRadius: '4px',
                  padding: '2px 6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  outline: 'none',
                  minWidth: '120px'
                }}
              />
            ) : (
              <span style={{ fontWeight: '500', color: '#374151' }}>
                {item.name}
              </span>
            )}
            
            {/* Show asset badge for lessons with linked assets */}
            {(!item.children || item.children.length === 0) && linkedAssets[item.id] && linkedAssets[item.id].length > 0 && (
              <span 
              style={{
                  background: '#dbeafe',
                  color: '#1e40af',
                  fontSize: '11px',
                  fontWeight: '600',
                  padding: '2px 6px',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '3px',
                cursor: 'pointer'
              }}
                title={`${linkedAssets[item.id].length} linked content assets: ${linkedAssets[item.id].map((link: any) => link.asset?.title || `Asset ${link.content_asset_id}`).join(', ')}`}
            >
                📎 {linkedAssets[item.id].length}
              </span>
            )}
          </div>

        </div>
        
        {/* Show compact asset list for lessons with linked assets */}
        {(!item.children || item.children.length === 0) && linkedAssets[item.id] && linkedAssets[item.id].length > 0 && (
          <div style={{ 
            marginLeft: (level * 8) + 24, 
            marginTop: '4px',
            marginBottom: '8px'
          }}>
            {linkedAssets[item.id].map((link: any, index: number) => (
              <div key={index} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '2px 0',
                fontSize: '11px',
                color: '#6b7280'
              }}>
                <span style={{ color: '#10b981' }}>•</span>
                <span style={{ fontWeight: '500', color: '#374151' }}>
                  {link.asset?.title || `Asset ${link.content_asset_id}`}
                </span>
                {link.asset?.content_type_name && (
                  <span style={{ 
                    color: '#9ca3af',
                    fontSize: '10px'
                  }}>
                    ({link.asset.content_type_name})
                  </span>
                )}
              </div>
            ))}
          </div>
        )}

        {expandedNodes.has(item.id) && item.children && (
          <div style={{ marginLeft: '8px' }}>
            {renderTreeView(item.children, level + 1)}
          </div>
        )}
      </div>
    ));
  };

  // Context Menu Component
  const renderContextMenu = () => {
    if (!contextMenu.visible || !contextMenu.item) return null;

    const isLesson = !contextMenu.item.children || contextMenu.item.children.length === 0;
    const childType = getChildTypeForParent(contextMenu.item);

    return (
      <div
        style={{
          position: 'fixed',
          top: contextMenu.y,
          left: contextMenu.x,
          backgroundColor: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '6px',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
          zIndex: 1000,
          minWidth: '180px',
          maxWidth: '220px',
          maxHeight: '300px',
          overflowY: 'auto',
          padding: '4px 0'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with structure info */}
        <div style={{
          padding: '8px 12px',
          borderBottom: '1px solid #e5e7eb',
          marginBottom: '4px',
          backgroundColor: '#f9fafb'
        }}>
          <div style={{ fontSize: '12px', fontWeight: '600', color: '#374151' }}>
            {contextMenu.item.name}
          </div>
          <div style={{ fontSize: '11px', color: '#6b7280' }}>
            {getLevelName(contextMenu.item.level)} • ID: {contextMenu.item.id}
          </div>
        </div>

        <button
          onClick={() => handleContextMenuAction('edit')}
          style={{
            width: '100%',
            padding: '8px 12px',
            border: 'none',
            background: 'none',
            textAlign: 'left',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
            color: '#374151',
            fontWeight: '500'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#eff6ff';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <FiEdit size={14} style={{ color: '#3b82f6' }} />
          Edit Structure
        </button>
        
        {/* Add Child Options */}
        {childType && (
          <button
            onClick={() => handleContextMenuAction(`add_${childType}`)}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: 'none',
              background: 'none',
              textAlign: 'left',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px',
              color: '#059669',
              fontWeight: '500'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f0fdf4';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <FiPlus size={14} style={{ color: '#059669' }} />
            Add {childType.charAt(0).toUpperCase() + childType.slice(1)}
          </button>
        )}
        
        {isLesson && (
          <button
            onClick={() => handleContextMenuAction('assets')}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: 'none',
              background: 'none',
              textAlign: 'left',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px',
              color: '#374151',
              fontWeight: '500'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#fef3c7';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <FiVideo size={14} style={{ color: '#f59e0b' }} />
            Manage Videos & Content
          </button>
        )}
        
        {!contextMenu.item.parent_id && (
          <>
            <button
              onClick={() => handleContextMenuAction('manage_books')}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: 'none',
                background: 'none',
                textAlign: 'left',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px',
                color: '#374151',
                fontWeight: '500'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#dbeafe';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <FiBook size={14} style={{ color: '#3b82f6' }} />
              Link Books to Structure
            </button>
            
            <button
              onClick={() => handleContextMenuAction('clone')}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: 'none',
                background: 'none',
                textAlign: 'left',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px',
                color: '#7c3aed',
                fontWeight: '500'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f5f3ff';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <FiCopy size={14} style={{ color: '#7c3aed' }} />
              Duplicate Structure
            </button>
          </>
        )}
        
        <hr style={{ margin: '8px 0', border: 'none', borderTop: '1px solid #e5e7eb' }} />
        
        {/* View Details Option */}
        <button
          onClick={() => {
            if (!contextMenu.item) return;
            
            // Log structure details to console for now
            const item = contextMenu.item;
            console.log('📋 Structure Details:', item);
            alert(`Structure: ${item.name}\nID: ${item.id}\nLevel: ${getLevelName(item.level)}\nGrade: ${item.grade_id || 'Not set'}\nSubject: ${item.subject_id || 'Not set'}\nChildren: ${item.children?.length || 0}`);
            setContextMenu({ ...contextMenu, visible: false });
          }}
          style={{
            width: '100%',
            padding: '8px 12px',
            border: 'none',
            background: 'none',
            textAlign: 'left',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
            color: '#374151',
            fontWeight: '500'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f3f4f6';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <FiInfo size={14} style={{ color: '#6b7280' }} />
          View Details
        </button>

        <hr style={{ margin: '8px 0', border: 'none', borderTop: '1px solid #e5e7eb' }} />
        
        <button
          onClick={() => handleContextMenuAction('delete')}
          style={{
            width: '100%',
            padding: '8px 12px',
            border: 'none',
            background: 'none',
            textAlign: 'left',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
            color: '#dc2626',
            fontWeight: '600'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#fef2f2';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <FiTrash2 size={14} style={{ color: '#dc2626' }} />
          Delete Structure
        </button>
      </div>
    );
  };



  // Event Handlers
  const handleEdit = (structure: ContentStructure) => {
    setEditingStructure(structure);
    setShowEditModal(true);
  };

  const handleDelete = (structure: ContentStructure) => {
    setDeletingStructure(structure);
    setShowDeleteModal(true);
  };

  const handleBulkOperation = (operation: 'delete' | 'move' | 'status') => {
    if (selectedStructures.size === 0) return;
    setShowBulkModal(true);
  };

  // Filter and Search
  const filteredStructures = structures.filter(structure => {
    const matchesSearch = structure.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      structure.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSubject = filterSubject === 'all' || structure.subject_id?.toString() === filterSubject;
    const matchesGrade = filterGrade === 'all' || structure.grade_id?.toString() === filterGrade;

    return matchesSearch && matchesSubject && matchesGrade;
  });

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <div style={{ fontSize: '32px', color: '#6b7280' }}>🔄</div>
        <p style={{ color: '#6b7280', fontSize: '16px' }}>Loading Enhanced Content Structure...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      {/* Compact Header */}
      <div style={{
        background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
        borderRadius: '12px',
        padding: '20px 24px',
        marginBottom: '24px',
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: '600', marginBottom: '4px' }}>
            Content Structure Management
        </h1>
          <p style={{ margin: 0, fontSize: '14px', opacity: 0.9 }}>
            Create and organize educational content hierarchies • 🖱️ Right-click structures for actions
        </p>
      </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <button
            onClick={() => setShowWizard(true)}
              style={{
              background: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
              border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '8px',
              padding: '10px 16px',
              fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
            }}
          >
            ➕ Create New Structure
            </button>
            <button
              onClick={() => setShowBulkModal(true)}
              style={{
              background: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
              border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '8px',
              padding: '10px 16px',
              fontSize: '14px',
                fontWeight: '500',
              cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
            }}
          >
            📋 Bulk Operations
            </button>
          </div>
        </div>

      {/* Toolbar */}
      <div style={{ 
        background: 'white',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        border: '1px solid #e5e7eb'
      }}>
        {/* Search and Filters */}
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <FiSearch style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#9ca3af'
            }} />
            <input
              type="text"
              placeholder="Search structures..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px 10px 40px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            />
          </div>

          <select
            value={filterGrade}
            onChange={(e) => handleMainGradeChange(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px'
            }}
          >
            <option value="all">All Grades</option>
            {grades.map(grade => (
              <option key={grade.id} value={grade.id}>{grade.name}</option>
            ))}
          </select>

          <select
            value={filterSubject}
            onChange={(e) => setFilterSubject(e.target.value)}
            disabled={filterGrade === 'all'}
            style={{
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              opacity: filterGrade === 'all' ? 0.5 : 1
            }}
          >
            <option value="all">All Subjects</option>
            {filteredSubjects.map(subject => (
              <option key={subject.id} value={subject.id}>{subject.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Content Display */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '24px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        border: '1px solid #e5e7eb'
      }}>
          <div>
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
              🌳 Hierarchical Tree View
            </h3>
          </div>
          {(() => {
            const filteredTree = filterTree(treeData, searchTerm, filterSubject, filterGrade);
            return renderTreeView(filteredTree);
          })()}
                  </div>
      </div>

      {/* Enhanced Progressive Creation Wizard */}
      {showWizard && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '800px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: '#374151' }}>
                Create New Content Structure - Step {currentStep} of 6
                </h2>
                <button
                  onClick={() => setShowWizard(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    color: '#6b7280'
                  }}
                >
                ×
                </button>
              </div>

            {/* Progress Bar */}
            <div style={{ marginBottom: '32px' }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                marginBottom: '8px' 
              }}>
                {[1, 2, 3, 4, 5, 6].map(step => (
                  <div
                    key={step}
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      backgroundColor: step <= currentStep ? '#10b981' : '#e5e7eb',
                      color: step <= currentStep ? 'white' : '#6b7280',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}
                  >
                    {step}
                  </div>
                ))}
              </div>
              <div style={{ 
                height: '4px', 
                backgroundColor: '#e5e7eb', 
                borderRadius: '2px',
                overflow: 'hidden'
              }}>
                <div style={{
                  height: '100%',
                  backgroundColor: '#10b981',
                  width: `${((currentStep - 1) / 5) * 100}%`,
                  transition: 'width 0.3s ease'
                }} />
              </div>
            </div>

            {/* Step Content */}
              {currentStep === 1 && (
                <div>
                  <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>
                  📚 Basic Information
                  </h3>
                  <div style={{ display: 'grid', gap: '16px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                        Structure Name *
                      </label>
                      <input
                        type="text"
                        value={wizardData.name}
                      onChange={(e) => setWizardData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Enter structure name (e.g., Mathematics Grade 7)"
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                        fontSize: '14px'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                        Description
                      </label>
                      <textarea
                        value={wizardData.description}
                      onChange={(e) => setWizardData(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Brief description of this content structure"
                        rows={3}
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                        fontSize: '14px',
                          resize: 'vertical'
                        }}
                      />
                    </div>
                </div>
              </div>
            )}

            {currentStep === 2 && (
              <div>
                <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>
                  🎯 Grade & Subject Selection
                </h3>
                <div style={{ display: 'grid', gap: '16px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                      Grade *
                      </label>
                      <select
                      value={wizardData.grade_id}
                      onChange={(e) => {
                        setWizardData(prev => ({ ...prev, grade_id: e.target.value, subject_id: '' }));
                        handleGradeChange(e.target.value);
                      }}
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    >
                      <option value="">Select Grade</option>
                      {grades.map(grade => (
                        <option key={grade.id} value={grade.id}>{grade.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                      Subject *
                      </label>
                      <select
                      value={wizardData.subject_id}
                      onChange={(e) => setWizardData(prev => ({ ...prev, subject_id: e.target.value }))}
                      disabled={!wizardData.grade_id}
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                        fontSize: '14px',
                        opacity: wizardData.grade_id ? 1 : 0.5
                      }}
                    >
                      <option value="">Select Subject</option>
                      {filteredSubjects.map(subject => (
                        <option key={subject.id} value={subject.id}>{subject.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}

            {currentStep === 3 && (
                <div>
                  <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>
                  📝 Topics
                  </h3>
                <p style={{ margin: '0 0 16px 0', color: '#6b7280', fontSize: '14px' }}>
                  Add topics for your content structure. Topics are the main sections.
                </p>
                {wizardData.topics.map((topic, topicIndex) => (
                  <div key={topic.id} style={{ 
                              border: '1px solid #e5e7eb',
                              borderRadius: '8px',
                              padding: '16px',
                    marginBottom: '16px' 
                  }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
                        Topic {topicIndex + 1}
                      </h4>
                      {wizardData.topics.length > 1 && (
                              <button
                                onClick={() => removeTopic(topic.id)}
                                style={{
                                  background: '#ef4444',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                            padding: '4px 8px',
                            fontSize: '12px',
                            cursor: 'pointer'
                                }}
                              >
                                Remove
                              </button>
                      )}
                            </div>
                            <div style={{ display: 'grid', gap: '12px' }}>
                                <input
                                  type="text"
                                  value={topic.name}
                                  onChange={(e) => updateTopic(topic.id, 'name', e.target.value)}
                        placeholder="Topic name"
                                  style={{
                                    width: '100%',
                          padding: '10px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '4px',
                                    fontSize: '14px'
                                  }}
                                />
                                <textarea
                                  value={topic.description}
                                  onChange={(e) => updateTopic(topic.id, 'description', e.target.value)}
                        placeholder="Topic description"
                                  rows={2}
                                  style={{
                                    width: '100%',
                          padding: '10px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '4px',
                                    fontSize: '14px',
                                    resize: 'vertical'
                                  }}
                                />
                            </div>
                          </div>
                        ))}
                <button
                  onClick={addTopic}
                  style={{
                    background: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '10px 16px',
                    fontSize: '14px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  + Add Topic
                </button>
                </div>
              )}

            {currentStep === 4 && (
                <div>
                  <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>
                  📖 Chapters
                  </h3>
                <p style={{ margin: '0 0 16px 0', color: '#6b7280', fontSize: '14px' }}>
                  Add chapters to your topics. Each topic can have multiple chapters.
                </p>
                      {wizardData.topics.map((topic, topicIndex) => (
                  <div key={topic.id} style={{ marginBottom: '24px' }}>
                          <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600', color: '#374151' }}>
                      {topic.name || `Topic ${topicIndex + 1}`}
                          </h4>
                            {topic.chapters.map((chapter, chapterIndex) => (
                              <div key={chapter.id} style={{
                        border: '1px solid #e5e7eb', 
                                borderRadius: '6px',
                        padding: '12px', 
                        marginBottom: '12px',
                        marginLeft: '16px'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                          <span style={{ fontSize: '14px', fontWeight: '500' }}>
                            Chapter {chapterIndex + 1}
                          </span>
                          <button
                            onClick={() => removeChapter(topic.id, chapter.id)}
                            style={{
                              background: '#ef4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              padding: '2px 6px',
                              fontSize: '11px',
                              cursor: 'pointer'
                            }}
                          >
                            Remove
                          </button>
                        </div>
                        <div style={{ display: 'grid', gap: '8px' }}>
                                <input
                                  type="text"
                                  value={chapter.name}
                                  onChange={(e) => updateChapter(topic.id, chapter.id, 'name', e.target.value)}
                            placeholder="Chapter name"
                                  style={{
                              width: '100%',
                              padding: '8px',
                                    border: '1px solid #d1d5db',
                              borderRadius: '4px',
                              fontSize: '13px'
                                  }}
                                />
                          <textarea
                                  value={chapter.description}
                                  onChange={(e) => updateChapter(topic.id, chapter.id, 'description', e.target.value)}
                            placeholder="Chapter description"
                            rows={2}
                                  style={{
                              width: '100%',
                              padding: '8px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '4px',
                              fontSize: '13px',
                              resize: 'vertical'
                                  }}
                          />
                        </div>
                              </div>
                            ))}
                            <button
                              onClick={() => addChapter(topic.id)}
                              style={{
                                background: '#3b82f6',
                                color: 'white',
                                border: 'none',
                        borderRadius: '4px',
                        padding: '6px 12px',
                        fontSize: '12px',
                                cursor: 'pointer',
                        marginLeft: '16px'
                              }}
                            >
                      + Add Chapter
                            </button>
                        </div>
                      ))}
                </div>
              )}

              {currentStep === 5 && (
                <div>
                  <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>
                  📚 Units
                  </h3>
                <p style={{ margin: '0 0 16px 0', color: '#6b7280', fontSize: '14px' }}>
                  Add units to your chapters. Each chapter can have multiple units.
                </p>
                {wizardData.topics.map((topic, topicIndex) => (
                  <div key={topic.id} style={{ marginBottom: '24px' }}>
                          <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600', color: '#374151' }}>
                      {topic.name || `Topic ${topicIndex + 1}`}
                          </h4>
                    {topic.chapters.map((chapter, chapterIndex) => (
                      <div key={chapter.id} style={{ marginBottom: '16px', marginLeft: '16px' }}>
                        <h5 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '500', color: '#6b7280' }}>
                          {chapter.name || `Chapter ${chapterIndex + 1}`}
                                </h5>
                        {chapter.units.map((unit, unitIndex) => (
                                    <div key={unit.id} style={{
                                      border: '1px solid #e5e7eb',
                            borderRadius: '6px', 
                                      padding: '12px',
                            marginBottom: '8px',
                            marginLeft: '16px'
                          }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                              <span style={{ fontSize: '13px', fontWeight: '500' }}>
                                Unit {unitIndex + 1}
                              </span>
                              <button
                                onClick={() => removeUnit(topic.id, chapter.id, unit.id)}
                                              style={{
                                  background: '#ef4444',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  padding: '2px 6px',
                                  fontSize: '11px',
                                  cursor: 'pointer'
                                }}
                              >
                                Remove
                              </button>
                            </div>
                            <div style={{ display: 'grid', gap: '8px' }}>
                      <input
                                              type="text"
                                value={unit.name}
                                onChange={(e) => updateUnit(topic.id, chapter.id, unit.id, 'name', e.target.value)}
                                placeholder="Unit name"
                        style={{
                          width: '100%',
                                  padding: '8px',
                          border: '1px solid #d1d5db',
                                  borderRadius: '4px',
                                  fontSize: '13px'
                                }}
                              />
                              <textarea
                                value={unit.description}
                                onChange={(e) => updateUnit(topic.id, chapter.id, unit.id, 'description', e.target.value)}
                                placeholder="Unit description"
                                rows={2}
                                              style={{
                                  width: '100%',
                                  padding: '8px',
                                  border: '1px solid #d1d5db',
                                                borderRadius: '4px',
                                  fontSize: '13px',
                                  resize: 'vertical'
                                              }}
                              />
                    </div>
                  </div>
                                        ))}
                                        <button
                          onClick={() => addUnit(topic.id, chapter.id)}
                                          style={{
                            background: '#8b5cf6',
                                            color: 'white',
                                            border: 'none',
                            borderRadius: '4px',
                            padding: '4px 8px',
                            fontSize: '11px',
                                            cursor: 'pointer',
                            marginLeft: '16px'
                                          }}
                                        >
                          + Add Unit
                                        </button>
                        </div>
                      ))}
                                                </div>
                                              ))}
                </div>
              )}

            {currentStep === 6 && (
                <div>
                  <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>
                  📝 Lessons & Content Assets
                  </h3>
                <p style={{ margin: '0 0 16px 0', color: '#6b7280', fontSize: '14px' }}>
                  Add lessons to your units and link content assets to each lesson.
                </p>
                {wizardData.topics.map((topic, topicIndex) => (
                  <div key={topic.id} style={{ marginBottom: '24px' }}>
                          <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600', color: '#374151' }}>
                      {topic.name || `Topic ${topicIndex + 1}`}
                          </h4>
                    {topic.chapters.map((chapter, chapterIndex) => (
                      <div key={chapter.id} style={{ marginBottom: '16px', marginLeft: '16px' }}>
                        <h5 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '500', color: '#6b7280' }}>
                          {chapter.name || `Chapter ${chapterIndex + 1}`}
                                </h5>
                        {chapter.units.map((unit, unitIndex) => (
                          <div key={unit.id} style={{ marginBottom: '12px', marginLeft: '16px' }}>
                            <h6 style={{ margin: '0 0 8px 0', fontSize: '13px', fontWeight: '500', color: '#9ca3af' }}>
                              {unit.name || `Unit ${unitIndex + 1}`}
                                      </h6>
                                        {unit.lessons.map((lesson, lessonIndex) => (
                                          <div key={lesson.id} style={{
                                border: '1px solid #e5e7eb',
                                            borderRadius: '6px',
                                padding: '12px',
                                marginBottom: '8px',
                                marginLeft: '16px'
                              }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                  <span style={{ fontSize: '12px', fontWeight: '500' }}>
                                    Lesson {lessonIndex + 1}
                                  </span>
                                  <button
                                    onClick={() => removeLesson(topic.id, chapter.id, unit.id, lesson.id)}
                                        style={{
                                      background: '#ef4444',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '4px',
                                      padding: '2px 6px',
                                      fontSize: '10px',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    Remove
                                  </button>
                                </div>
                                <div style={{ display: 'grid', gap: '8px' }}>
                                            <input
                                              type="text"
                                              value={lesson.name}
                                              onChange={(e) => updateLesson(topic.id, chapter.id, unit.id, lesson.id, 'name', e.target.value)}
                                    placeholder="Lesson name"
                                              style={{
                                      width: '100%',
                                      padding: '6px',
                                                border: '1px solid #d1d5db',
                                      borderRadius: '4px',
                                      fontSize: '12px'
                                              }}
                                            />
                                  <textarea
                                              value={lesson.description}
                                              onChange={(e) => updateLesson(topic.id, chapter.id, unit.id, lesson.id, 'description', e.target.value)}
                                    placeholder="Lesson description"
                                    rows={2}
                                              style={{
                                      width: '100%',
                                      padding: '6px',
                                                border: '1px solid #d1d5db',
                                          borderRadius: '4px',
                                      fontSize: '12px',
                                      resize: 'vertical'
                                    }}
                                  />
                                  <div>
                                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '11px', fontWeight: '500' }}>
                                      Content Assets
                                    </label>
                                    <ContentAssetSelector
                                      gradeId={wizardData.grade_id}
                                      subjectId={wizardData.subject_id}
                                      selectedAssetIds={lesson.content_asset_ids}
                                      onSelectionChange={(assetIds) => updateLesson(topic.id, chapter.id, unit.id, lesson.id, 'content_asset_ids', assetIds)}
                                    />
                                  </div>
                                </div>
                                    </div>
                                  ))}
                                            <button
                              onClick={() => addLesson(topic.id, chapter.id, unit.id)}
                                              style={{
                                background: '#f59e0b',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '4px',
                                padding: '4px 8px',
                                fontSize: '10px',
                                                cursor: 'pointer',
                                marginLeft: '16px'
                                              }}
                                            >
                              + Add Lesson
                                            </button>
                                          </div>
                                        ))}
                              </div>
                    ))}
                        </div>
                      ))}
                    </div>
                  )}

            {/* Navigation Buttons */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              marginTop: '32px',
              paddingTop: '24px',
              borderTop: '1px solid #e5e7eb'
            }}>
                                        <button
                onClick={prevStep}
                disabled={currentStep === 1}
                                          style={{
                  background: currentStep === 1 ? '#e5e7eb' : '#6b7280',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                  padding: '12px 24px',
                                            fontSize: '14px',
                  fontWeight: '500',
                  cursor: currentStep === 1 ? 'not-allowed' : 'pointer'
                                          }}
                                        >
                Previous
                                        </button>

              <div style={{ display: 'flex', gap: '12px' }}>
                {currentStep < 6 ? (
                <button
                  onClick={nextStep}
                    disabled={
                      (currentStep === 1 && !wizardData.name.trim()) ||
                      (currentStep === 2 && (!wizardData.grade_id || !wizardData.subject_id))
                    }
                  style={{
                      background: (
                        (currentStep === 1 && !wizardData.name.trim()) ||
                        (currentStep === 2 && (!wizardData.grade_id || !wizardData.subject_id))
                      ) ? '#e5e7eb' : '#10b981',
                    color: 'white',
                    border: 'none',
                          borderRadius: '6px',
                    padding: '12px 24px',
                      fontSize: '14px',
                    fontWeight: '500',
                      cursor: (
                        (currentStep === 1 && !wizardData.name.trim()) ||
                        (currentStep === 2 && (!wizardData.grade_id || !wizardData.subject_id))
                      ) ? 'not-allowed' : 'pointer'
                  }}
                >
                  Next
                </button>
              ) : (
                <button
                  onClick={submitWizard}
                    disabled={!wizardData.name.trim() || !wizardData.grade_id || !wizardData.subject_id}
                  style={{
                      background: (!wizardData.name.trim() || !wizardData.grade_id || !wizardData.subject_id) 
                        ? '#e5e7eb' : '#059669',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '12px 24px',
                      fontSize: '14px',
                    fontWeight: '500',
                      cursor: (!wizardData.name.trim() || !wizardData.grade_id || !wizardData.subject_id) 
                        ? 'not-allowed' : 'pointer'
                  }}
                >
                    Create Structure
                </button>
                                                  )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                  )}

      {/* Context Menu */}
      {renderContextMenu()}

      {/* Add Child Modal */}
      {addChildModalOpen && (
                  <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
                    <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#374151' }}>
                Add {addChildData.childType.charAt(0).toUpperCase() + addChildData.childType.slice(1)}
              </h3>
            <button
                onClick={() => setAddChildModalOpen(false)}
              style={{
                background: 'none',
                border: 'none',
                  fontSize: '20px',
                cursor: 'pointer',
                  color: '#6b7280'
              }}
            >
                ×
            </button>
                    </div>

            <div style={{ marginBottom: '20px' }}>
              <p style={{ margin: '0 0 16px 0', color: '#6b7280', fontSize: '14px' }}>
                {addChildData.parentItem ? (
                  <>Adding {addChildData.childType} to: <strong>{addChildData.parentItem.name}</strong></>
                ) : (
                  <>Creating new {addChildData.childType}</>
                )}
              </p>
              
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151' }}>
                {addChildData.childType.charAt(0).toUpperCase() + addChildData.childType.slice(1)} Name *
              </label>
                                      <input
                                        type="text"
                value={addChildData.childName}
                onChange={(e) => setAddChildData(prev => ({ ...prev, childName: e.target.value }))}
                placeholder={`Enter ${addChildData.childType} name`}
                                        style={{
                  width: '100%',
                  padding: '12px',
                                          border: '1px solid #d1d5db',
                                          borderRadius: '6px',
                                          fontSize: '14px'
                                        }}
                autoFocus
              />
                                                </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                                      <button
                onClick={() => setAddChildModalOpen(false)}
                                        style={{
                  background: '#6b7280',
                                          color: 'white',
                                          border: 'none',
                  borderRadius: '6px',
                  padding: '10px 20px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                Cancel
                                      </button>
                                  <button
                onClick={handleCreateChild}
                disabled={!addChildData.childName.trim()}
                                    style={{
                  background: addChildData.childName.trim() ? '#059669' : '#9ca3af',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '6px',
                  padding: '10px 20px',
                                      fontSize: '14px',
                  cursor: addChildData.childName.trim() ? 'pointer' : 'not-allowed'
                                    }}
                                  >
                Create {addChildData.childType.charAt(0).toUpperCase() + addChildData.childType.slice(1)}
                                  </button>
                                </div>
                              </div>
                    </div>
                  )}

      {/* Asset Management Modal */}
      {assetModalOpen && selectedLessonForAssets && (
                  <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
                    <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '800px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#374151' }}>
                Manage Assets for: {selectedLessonForAssets.name}
              </h3>
              <button
                onClick={() => {
                  setAssetModalOpen(false);
                  setSelectedLessonForAssets(null);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer',
                  color: '#6b7280'
                }}
              >
                ×
              </button>
                    </div>

            {/* Currently Linked Assets */}
            <div style={{ marginBottom: '24px' }}>
                          <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600', color: '#374151' }}>
                Currently Linked Assets ({linkedAssets[selectedLessonForAssets.id]?.length || 0})
                          </h4>
              {linkedAssets[selectedLessonForAssets.id] && linkedAssets[selectedLessonForAssets.id].length > 0 ? (
                <div style={{ display: 'grid', gap: '8px' }}>
                  {linkedAssets[selectedLessonForAssets.id].map((link: any) => (
                    <div key={link.id} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px',
                      border: '1px solid #e5e7eb',
                              borderRadius: '6px',
                      backgroundColor: '#f9fafb'
                    }}>
                      <div>
                        <div style={{ fontWeight: '500', fontSize: '14px' }}>
                          {link.asset?.title || `Asset ${link.content_asset_id}`}
                        </div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>
                          Type: {link.asset?.content_type?.name || 'Unknown'}
                        </div>
                      </div>
                <button
                        onClick={() => handleRemoveAssetFromLesson(link.id)}
                  style={{
                          background: '#ef4444',
                    color: 'white',
                    border: 'none',
                          borderRadius: '4px',
                          padding: '6px 12px',
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                      >
                        Remove
                </button>
                    </div>
                  ))}
                            </div>
                          ) : (
                <p style={{ color: '#6b7280', fontSize: '14px', margin: 0 }}>
                  No assets currently linked to this lesson.
                </p>
              )}
            </div>

            {/* Add Assets Section */}
            <div>
              <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600', color: '#374151' }}>
                Add Assets
              </h4>
              {loadingAssets ? (
                <p style={{ color: '#6b7280', fontSize: '14px' }}>Loading available assets...</p>
              ) : availableAssets.length > 0 ? (
                <div style={{ display: 'grid', gap: '8px', maxHeight: '300px', overflow: 'auto' }}>
                  {availableAssets.map((asset: any) => {
                    const isAlreadyLinked = linkedAssets[selectedLessonForAssets.id]?.some(
                      (link: any) => link.content_asset_id === asset.id
                    );
                    
                    return (
                      <div key={asset.id} style={{
                                      display: 'flex',
                        justifyContent: 'space-between',
                                      alignItems: 'center',
                                      padding: '12px',
                        border: '1px solid #e5e7eb',
                                      borderRadius: '6px',
                        backgroundColor: isAlreadyLinked ? '#f3f4f6' : 'white',
                        opacity: isAlreadyLinked ? 0.6 : 1
                      }}>
                        <div>
                          <div style={{ fontWeight: '500', fontSize: '14px' }}>
                            {asset.title}
                          </div>
                          <div style={{ fontSize: '12px', color: '#6b7280' }}>
                            Type: {asset.content_type_name} | Grade: {asset.grade_name}
                          </div>
                        </div>
                <button
                          onClick={() => handleAddAssetToLesson(asset.id)}
                          disabled={isAlreadyLinked}
                  style={{
                            background: isAlreadyLinked ? '#9ca3af' : '#059669',
                    color: 'white',
                    border: 'none',
                                          borderRadius: '4px',
                            padding: '6px 12px',
                            fontSize: '12px',
                            cursor: isAlreadyLinked ? 'not-allowed' : 'pointer'
                                        }}
                                      >
                          {isAlreadyLinked ? 'Already Linked' : 'Add'}
                </button>
            </div>
                    );
                  })}
          </div>
              ) : (
                <p style={{ color: '#6b7280', fontSize: '14px', margin: 0 }}>
                  No content assets available. Please upload assets in Content Library first.
                </p>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
            <button
                onClick={() => {
                  setAssetModalOpen(false);
                  setSelectedLessonForAssets(null);
                }}
              style={{
                  background: '#6b7280',
                color: 'white',
                                      border: 'none',
                                      borderRadius: '6px',
                  padding: '10px 20px',
                                      fontSize: '14px',
                  cursor: 'pointer'
                                    }}
                                  >
                Close
            </button>
                                </div>
          </div>
        </div>
      )}

      {/* Structure Edit Modal */}
      {structureEditModalOpen && editingStructureData && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#374151' }}>
                Edit Structure
              </h3>
              <button
                onClick={handleStructureEditCancel}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer',
                  color: '#6b7280'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                Structure Name
              </label>
              <input
                type="text"
                value={editingStructureData.name}
                onChange={(e) => setEditingStructureData(prev => prev ? { ...prev, name: e.target.value } : null)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
                placeholder="Enter structure name"
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                Grade
              </label>
              <select
                value={editingStructureData.grade_id}
                onChange={(e) => {
                  const gradeId = parseInt(e.target.value);
                  setEditingStructureData(prev => prev ? { ...prev, grade_id: gradeId, subject_id: 0 } : null);
                  // Filter subjects based on selected grade
                  if (gradeId > 0) {
                    handleEditModalGradeChange(gradeId);
                  }
                }}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              >
                <option value={0}>Select Grade</option>
                {grades.map(grade => (
                  <option key={grade.id} value={grade.id}>
                    {grade.name}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                Subject
              </label>
              <select
                value={editingStructureData.subject_id}
                onChange={(e) => setEditingStructureData(prev => prev ? { ...prev, subject_id: parseInt(e.target.value) } : null)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
                disabled={editingStructureData.grade_id === 0}
              >
                <option value={0}>Select Subject</option>
                {editModalFilteredSubjects.map(subject => (
                  <option key={subject.id} value={subject.id}>
                    {subject.name}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                onClick={handleStructureEditCancel}
                style={{
                  background: '#6b7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '10px 20px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleStructureEditSave}
                disabled={!editingStructureData.name.trim() || editingStructureData.grade_id === 0 || editingStructureData.subject_id === 0}
                style={{
                  background: editingStructureData.name.trim() && editingStructureData.grade_id > 0 && editingStructureData.subject_id > 0 
                    ? '#059669' : '#9ca3af',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '10px 20px',
                  fontSize: '14px',
                  cursor: editingStructureData.name.trim() && editingStructureData.grade_id > 0 && editingStructureData.subject_id > 0 
                    ? 'pointer' : 'not-allowed'
                }}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Book Content Manager */}
      <BookContentManager
        isOpen={bookContentModalOpen}
        onClose={() => {
          setBookContentModalOpen(false);
          setSelectedStructureForBooks(null);
        }}
        selectedStructure={selectedStructureForBooks}
      />
    </div>
  );
};

export default EnhancedContentStructure;
